import { parseControlledDevice, parseControllerDevice, parseEmcDeviceLink, parseStorageDeviceLink } from '../../parsers/device-parsers/device-parsers'
import { useAccountLinks, useInstallation, useInstallationDevices, useInstallationSubDevices } from './installation-fetch-hooks'
import { AccountLinkDto, DeviceLinkDto, DeviceStatus, SubDevicesDto } from '../api'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Device } from '../forms'
import { TFunction } from 'i18next'
import { useUser } from 'src/Hooks/useUser'
import { useMemo } from 'react'
import { fromMonitoringMeterDeviceDtoToModel } from '../../advanced-settings/powermeter'

export const useInstallationName = (installationId: number | undefined): string | undefined => {
  return useInstallation(installationId).data?.projectName ?? undefined
}

export const useInstallationHasEmc = (installationId: number | undefined): boolean => {
  return !!useInstallationDevices(installationId).data?.emc?.deviceId
}

export const useInstallationDevicesList = (
  installationId: number | undefined,
  enabled: boolean,
  /** Useful if emc id is already pre-fetched. */
  emcMasterId?: number | null
): { data: Device[]; isLoading: boolean } => {
  const { t } = useCustomTranslation()

  const { data: devices, isLoading: devicesLoading } = useInstallationDevices(installationId, enabled)

  const emcId: string | null = emcMasterId?.toString() ?? devices?.emc?.id?.toString() ?? null

  const { data: subDevices, isLoading: subDevicesLoading } = useInstallationSubDevices(installationId, emcId, enabled)

  const isLoading = devicesLoading || subDevicesLoading

  return {
    data: [...parseDeviceLinks(devices?.activeDevices ?? [], t), ...parseSubDevices(subDevices)],
    isLoading,
  }
}

const parseDeviceLinks = (deviceLinks: Nullable<DeviceLinkDto>[], t: TFunction): Device[] => {
  return deviceLinks.flatMap(deviceLink => {
    if (deviceLink.deviceType === 'emc') return [parseEmcDeviceLink(deviceLink)] as Device[]
    if (deviceLink.deviceType === 'storage') return [parseStorageDeviceLink(deviceLink, t)]
    return []
  })
}

const parseSubDevices = (subDevicesDto: Nullable<SubDevicesDto> | undefined): Device[] => {
  if (!subDevicesDto) return []
  const { controlled, controllers, monitoring } = subDevicesDto
  const heatpump = controllers?.find(controller => controller.type === 'HpWithISG')
  const ioDevices = controllers?.filter(controller => controller.type !== 'HpWithISG')

  return [
    ...(heatpump ? [parseControllerDevice(heatpump)] : []),
    ...(controlled?.map(evse => parseControlledDevice(evse)) ?? []),
    ...(monitoring?.meters
      ?.map(meter => fromMonitoringMeterDeviceDtoToModel(meter))
      .sort((a, b) => Number(a.pairingId || 0) - Number(b.pairingId || 0)) ?? []),
    ...(ioDevices?.map(io => parseControllerDevice(io)) ?? []),
  ]
}

/** Maps device to its status values based on (parsed) deviceId (deviceId is generated on the server). */
export type DeviceStatusMap = Map<
  string,
  {
    status: DeviceStatus
    statusBits: number | null
  }
>

/**
 * Hook that is used in the Advanced Settings page to return the latest
 * device statuses from the server.
 *
 * This is needed to display the latest device statuses in the devices table/list
 * independantly from the installation form's values.
 */
export const useInstallationDeviceStatuses = (installationId: number | null, enabled = true): DeviceStatusMap => {
  const { t } = useCustomTranslation()

  const { data: devices } = useInstallationDevices(installationId ?? undefined, enabled)
  const emcId: string | null = devices?.emc?.id?.toString() ?? null
  const { data: subDevices } = useInstallationSubDevices(installationId ?? undefined, emcId, enabled)

  const statusPerDeviceId = useMemo(() => {
    const allDevices = [...parseDeviceLinks(devices?.activeDevices ?? [], t), ...parseSubDevices(subDevices)]

    return new Map(allDevices.map(datum => [datum.deviceId, { status: datum.deviceStatus, statusBits: datum.deviceStatusCodeBits }]))
  }, [devices, subDevices, t])

  return statusPerDeviceId
}

export const useCurrentAccountLink = (installationId: number | undefined): Nullable<AccountLinkDto> | undefined => {
  const { userData: user } = useUser()

  const { data: accounts } = useAccountLinks(installationId)

  return accounts?.find(account => account.accountId === user?.ssoId)
}
